// V1 parity: GC-side watcher that detects when a client accepts an estimate
// via the schema-v2 portallink__ doc (portalEstimate.portalStatus === 'accepted').
//
// BACKGROUND — Two parallel paths for the same signal:
//
//   1. usePortalResponses (Shell.tsx) reads the top-level `status` field that
//      V1 / early V2 portal.ts writes on the portallink__ doc.  That field is
//      NOT present on schema-v2 docs (schemaVersion: 2) created by portal.ts
//      during the V1-parity rebuild.  So usePortalResponses silently misses
//      acceptances on the new schema.
//
//   2. THIS hook reads `portalEstimate.portalStatus` — the structured field
//      that portal.ts / portal.html (client side) stamps when a client clicks
//      Accept.  It is the canonical V2 acceptance signal.
//
// The two hooks are complementary, not redundant:
//   • usePortalResponses handles legacy tokens (old schema, no portalEstimate).
//   • usePortalLinkEstimateAccept handles schema-v2 tokens.
//
// WHAT HAPPENS ON ACCEPT (V1 parity — public/app/index.html L21024-21025,
// L21296-21302):
//   1. Flip local Estimate.clientPortalStatus → 'accepted'.
//   2. If Estimate.approvalStatus was 'pending', auto-promote → 'approved'
//      (V1 L21296: "pending" === t.approvalStatus && (t.approvalStatus = "approved")).
//   3. Stamp approvedAt / approvedBy = '<clientName> via portal'.
//   4. Log an activity entry (V1 L9328: logActivity("estimate", …)).
//   5. Show a toast banner (V1 L21025: showBanner("✅ … accepted the estimate")).
//   6. Persist to Firestore via saveAppState (activity log changed) or saveSlice.
//
// WHAT DOES NOT HAPPEN (explicitly NOT mirrored):
//   • Auto-convert the estimate to a project.  V1 shows a "go to Estimates to
//     create the project" prompt — project creation stays a separate explicit GC
//     action (handleAcceptIncoming / openApproveEstimateModal).
//
// IDEMPOTENCY — local-state-first (pattern from commit 59a534b):
//   • Primary guard: skip if local Estimate.clientPortalStatus === 'accepted'.
//     This prevents double-apply across reloads and session re-mounts.
//   • Secondary (session) guard: appliedRef.current (Set<token>) blocks
//     double-toast / double-bump if the same snapshot fires twice in a session.
//   • The Firestore-side portalEstimate.portalStatus is NOT used as the primary
//     guard: if saveAppState fails after Firestore has been updated, the next
//     snapshot sees Firestore-accepted + local-pending and self-heals.
//
// SERIALIZATION: every snapshot callback runs inside the same queueRef promise
// chain so two acceptances (unlikely but possible) don't race on saveAppState.
//
// ARCHITECTURAL PATTERN: identical to usePortalLinkCOApproval (Task 5).

import { useEffect, useRef } from 'react'
import { onSnapshot, query, where } from 'firebase/firestore'
import { sharedCol } from '../lib/firebase/firestore'
import { useAuth } from './useAuth'
import { useQueryClient } from '@tanstack/react-query'
import { qk } from './queryKeys'
import { saveAppState } from '../lib/db'
import { toastOk } from '../lib/toastStore'
import { makeActivityLogEntry } from '../lib/activityLog'
import type { AppState } from '../types/firestore'
import type { Estimate } from '../types/domain'

// ── Local types ────────────────────────────────────────────────────────────────

interface PortalEstimateLite {
  portalStatus?: string
  acceptedAt?: number
}

interface PortalDocLite {
  token: string
  ownerUid?: string
  /** estId written by portal.ts (L168: estId: estimate.id). */
  estId?: number
  estName?: string
  clientName?: string
  projId?: number | null
  portalEstimate?: PortalEstimateLite
}

// ── Pure decision helper (exported so unit tests can import it) ────────────────

/**
 * Returns true if the portallink doc signals acceptance AND the local estimate
 * hasn't already been flipped.
 *
 * Pure function: no side effects, no hooks.  Unit-testable without React or
 * Firestore.
 *
 * @param doc       - Minimal portallink__ doc shape.
 * @param localEst  - Current local Estimate (may be undefined if not found).
 */
export function shouldPromoteEstimateAccept(
  doc: PortalDocLite,
  localEst: Estimate | undefined,
): boolean {
  // No portalEstimate field → legacy schema; usePortalResponses handles it.
  if (!doc.portalEstimate) return false
  // Not accepted → nothing to do.
  if (doc.portalEstimate.portalStatus !== 'accepted') return false
  // No matching local estimate → stale or deleted; skip.
  if (!localEst) return false
  // LOCAL-STATE-FIRST GUARD: already flipped → idempotent skip.
  if (localEst.clientPortalStatus === 'accepted') return false
  return true
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function usePortalLinkEstimateAccept(): void {
  const { effectiveUid: uid, isReady } = useAuth()
  const qc = useQueryClient()

  // Prevents double-toast / double-bump within one session for the same token.
  const appliedRef = useRef<Set<string>>(new Set())

  // Serializes snapshot callbacks so back-to-back acceptances don't race on
  // saveAppState (identical pattern to usePortalLinkCOApproval).
  const queueRef = useRef<Promise<void>>(Promise.resolve())

  useEffect(() => {
    if (!uid || !isReady) return

    const q = query(sharedCol(), where('ownerUid', '==', uid))

    const unsub = onSnapshot(
      q,
      (snap) => {
        // Collect portallink__ docs from this snapshot.
        const docs: PortalDocLite[] = []
        snap.forEach((docSnap) => {
          if (!docSnap.id.startsWith('portallink__')) return
          const d = docSnap.data() as Partial<PortalDocLite>
          const entry: PortalDocLite = {
            token: docSnap.id.slice('portallink__'.length),
          }
          if (d.ownerUid)   entry.ownerUid   = d.ownerUid
          if (d.estId !== undefined) entry.estId = d.estId
          if (d.estName)    entry.estName    = d.estName
          if (d.clientName) entry.clientName = d.clientName
          if (d.projId !== undefined) entry.projId = d.projId
          if (d.portalEstimate) entry.portalEstimate = d.portalEstimate
          docs.push(entry)
        })

        queueRef.current = queueRef.current.then(async () => {
          // Read LIVE state at callback time — not captured at effect-mount.
          // Closure-capturing `state` would miss estimates created after mount.
          const s = qc.getQueryData<AppState>(qk.appstate(uid))
          if (!s) return

          // ── Collect new acceptances ────────────────────────────────────────
          interface Pending {
            token: string
            estId: number
            clientName: string
            acceptedAt: number
            localEst: Estimate
          }
          const pending: Pending[] = []

          for (const d of docs) {
            // Session-level guard (double-toast prevention).
            if (appliedRef.current.has(d.token)) continue

            // Find the local estimate by estId (primary) or by portalToken membership.
            let localEst: Estimate | undefined
            if (d.estId !== undefined) {
              localEst = s.estimates.find((e) => e.id === d.estId)
            }
            // Fallback: scan portalTokens (covers tokens minted before estId was
            // written reliably, or for old docs missing the field).
            if (!localEst) {
              localEst = s.estimates.find((e) =>
                Array.isArray(e.portalTokens) && e.portalTokens.includes(d.token),
              )
            }

            if (!shouldPromoteEstimateAccept(d, localEst)) continue
            // localEst is guaranteed non-undefined after shouldPromoteEstimateAccept.
            const est = localEst!

            pending.push({
              token: d.token,
              estId: est.id,
              clientName: d.clientName || 'Client',
              acceptedAt: d.portalEstimate?.acceptedAt ?? Date.now(),
              localEst: est,
            })
          }

          if (pending.length === 0) {
            console.log('[portalLinkEstimateAccept] snapshot: no new acceptances (applied:', appliedRef.current.size, ')')
            return
          }

          console.log('[portalLinkEstimateAccept] promoting', pending.length, 'acceptance(s):',
            pending.map((p) => `est-${p.estId}(${p.clientName})`).join(', '))

          try {
            // ── Flip local estimates + build activity log entries ─────────────
            // V1 L21024-21025, L21296-21302:
            //   • clientPortalStatus = 'accepted'
            //   • if approvalStatus === 'pending' → auto-promote to 'approved'
            //     and stamp approvedAt / approvedBy
            //   • logActivity("estimate", 'Estimate "..." was accepted by ...', "")
            // DIAG: dump before-state of affected estimates so we can see if a
            // watcher elsewhere is wiping cost/qty. Safe to leave in for now —
            // only fires on the first acceptance per session per token.
            for (const p of pending) {
              const dump = p.localEst.categories?.flatMap((c) => (c.items ?? []).map((it) => {
                const amt = (it as unknown as { amount?: number }).amount
                return { cat: c.name, desc: it.desc, cost: it.cost, qty: it.qty, amount: amt }
              }))
              console.log('[ZEROOUT-DIAG] accept fires for est-' + p.estId, dump)
            }
            let next: AppState = s

            for (const p of pending) {
              const nowIso = new Date(p.acceptedAt).toISOString()

              const newEstimates: Estimate[] = next.estimates.map((e): Estimate => {
                if (e.id !== p.estId) return e
                const u: Estimate = { ...e, clientPortalStatus: 'accepted' }

                // V1 L21296: auto-promote if internal approval was 'pending'.
                if ((e.approvalStatus ?? 'draft') === 'pending') {
                  u.approvalStatus = 'approved'
                  u.approvedAt     = nowIso
                  u.approvedBy     = `${p.clientName} via portal`
                }

                return u
              })

              // V1 L9328: logActivity("estimate", 'Estimate "c" was accepted by p', "")
              // V2 shape: kind='estimate.portal_accepted' mirrors useEstimateMutations.changeStatus.
              const estName = p.localEst.name || 'estimate'
              const entry = makeActivityLogEntry(
                uid,
                'estimate.portal_accepted',
                p.estId,
                `Estimate "${estName}" was accepted by ${p.clientName}`,
                {
                  estName,
                  clientName: p.clientName,
                  source: 'portal',
                },
              )

              next = {
                ...next,
                estimates: newEstimates,
                activityLog: [...next.activityLog, entry],
              }
            }

            // Mark session-applied BEFORE the async save to block re-entrancy
            // within this session (even if saveAppState is slow).
            for (const p of pending) {
              appliedRef.current.add(p.token)
            }

            // ── Persist ────────────────────────────────────────────────────────
            // Activity log was always mutated, so use saveAppState (not saveSlice)
            // so the activityLog slice is persisted too.
            qc.setQueryData(qk.appstate(uid), next)
            await saveAppState(uid, next)
            console.log('[portalLinkEstimateAccept] saveAppState complete')

            // ── Toasts ─────────────────────────────────────────────────────────
            // V1 L21025: showBanner("✅ " + clientName + " accepted the estimate")
            // Fire after save so a crash during save doesn't show a false toast.
            for (const p of pending) {
              const estName = p.localEst.name || 'estimate'
              toastOk(`${p.clientName} accepted "${estName}" — go to Estimates to create the project`)
            }
          } catch (err) {
            console.warn('[portalLinkEstimateAccept] apply failed', err)
            // On failure, remove from appliedRef so the next snapshot retries
            // (cross-session recovery is handled by the local-state guard in
            // shouldPromoteEstimateAccept; this just helps within the session).
            for (const p of pending) {
              appliedRef.current.delete(p.token)
            }
          }
        })
      },
      (err) => {
        console.warn('[portalLinkEstimateAccept] listener error', err)
      },
    )

    return () => {
      unsub()
      appliedRef.current.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, isReady])
}
